"use client";

import React, { useEffect, useState } from "react";
import { Drawer, Box, Typography } from "@mui/material";
import Image from "next/image";
import { HomeProduct, Quantity, SkuDetail, SkuImages } from "@/types/home";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import TechnicalData from "./TechnicalData";
import ImageCarousel from "./ImageCarousel";
import VarientSelection from "./VarientSelection";
import AddToCart from "./AddToCart";
import PriceDetail from "./PriceDetail";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { getCurrentQuantity } from "@/utils/helpers";
import { fetchProductsWithProductID } from "@/store/slices/homeSlice";
const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

// Props Interface
interface ProductDetailProps {
  isOpen: boolean;
  onClose: () => void;
  product: HomeProduct;
  selected: "ndp" | "sales";
}

const ProductDetail: React.FC<ProductDetailProps> = ({
  isOpen,
  onClose,
  product,
  selected,
}) => {
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState<string | false>("");

  // Get selected SKU from Redux
  const { selectedSKU, productDetail, loading } = useAppSelector(
    (state) => state.home
  );

  useEffect(() => {
    if (product?.prod_ID) {
      dispatch(fetchProductsWithProductID(product?.prod_ID));
    }
  }, [dispatch, product?.prod_ID]);
  const { cart } = useAppSelector((state) => state.cart);

  const currentQuantity =
    getCurrentQuantity(selectedSKU ?? product?.SKU_ID, cart) || 0;

  // Find the SKU details based on the selected SKU ID
  const skuDetails: SkuDetail[] =
    productDetail?.products?.[0]?.skuDetails || [];
  const skuImages: SkuImages[] = productDetail?.products?.[0]?.SKU_Images || [];
  const skuQtys: Quantity[] = productDetail?.quantity || [];
  const selectedSKUDetail = skuDetails?.find(
    (sku) => sku?.SKU_ID === selectedSKU
  );
  const selectedSKUImage = skuImages?.find(
    (sku) => sku?.SKU_ID === selectedSKU
  );
  const availableQty = skuQtys?.find((sku) => sku?.SKU_ID === selectedSKU)?.qty;

  // Determine the product name and image to display based on SKU selection
  const displayedProductName =
    selectedSKUDetail?.prod_name || product?.prod_name;
  const skuNDP_price =
    (selected === "sales" &&
    selectedSKUDetail?.Sales_Qunatity !== null &&
    selectedSKUDetail?.Sales_Qunatity !== 0 &&
    selectedSKUDetail?.Sales_Price !== null &&
    Number(selectedSKUDetail?.Sales_Price) !== 0
      ? selectedSKUDetail?.Sales_Price
      : selectedSKUDetail?.NDP_Price) ||
    (selected === "sales" &&
    currentQuantity &&
    product?.Sales_Qunatity !== null &&
    product?.Sales_Qunatity !== 0 &&
    product?.Sales_Price !== null &&
    Number(product?.Sales_Price) !== 0
      ? product?.Sales_Price
      : product?.NDP_price);
  const displayedImage = selectedSKUImage?.images || product.Image;

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "100%" },
          height: "100vh",
          bgcolor: "transparent",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "flex-start",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
          bottom: "0",
        },
      }}
    >
      <Box
        width="65%"
        height="100vh"
        position="sticky"
        top={0}
        px={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRight="1px solid #e7e5eb"
        sx={{ background: "transparent !important" }}
        onClick={onClose}
      >
        <ImageCarousel displayedImage={displayedImage} onClose={onClose} />
      </Box>

      <Box
        width="35%"
        minHeight="100vh"
        position="relative"
        sx={{
          background: "#f7f7f7",
          pointerEvents: loading ? "none" : "auto", // Disable interaction when loading
          opacity: loading ? 0.9 : 1, // Optional: Reduce opacity to indicate loading
        }}
      >
        <Box width="100%" p={2} minHeight="100vh">
          {/* Stage 2: Product Name and Brand Logo */}
          <Box width="100%">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              bgcolor="#f7f7f7"
            >
              <Typography
                fontWeight="bold"
                fontSize="15px"
                display="flex"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <ChevronLeftIcon
                  style={{
                    color: "black",
                    marginRight: "5px",
                    cursor: "pointer",
                    fontSize: "20px",
                  }}
                  onClick={onClose}
                />
                {displayedProductName}
              </Typography>
              <Image
                quality={50}
                property="no"
                unoptimized
                src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_MANUFACTURE_IMAGE/${product.manufacture_images}`}
                alt="Brand Logo"
                width={70}
                height={30}
                style={{
                  marginLeft: "5px",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          <PriceDetail
            handleChange={handleChange}
            product={product}
            skuNDP_price={skuNDP_price}
            currentQuantity={currentQuantity}
          />
          {skuDetails?.length > 1 && (
            <VarientSelection
              expanded={expanded}
              handleChange={handleChange}
              product={product}
            />
          )}

          <TechnicalData expanded={expanded} handleChange={handleChange} />
        </Box>
        <AddToCart
          product={product}
          availableQty={availableQty && availableQty}
          currentQuantity={currentQuantity}
          selected={selected}
        />
      </Box>
    </Drawer>
  );
};

export default ProductDetail;
